/**
 * POS Reports - Sales and inventory reports for Point of Sale
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BarChart3, ArrowLeft, Calendar, Download, TrendingUp,
  DollarSign, Package, ShoppingCart, CreditCard, Banknote,
  RefreshCw, AlertTriangle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { posAPI } from '../../services/api';
import { toast } from 'sonner';

// Stat card
const StatCard = ({ icon: Icon, label, value, subValue, color = "text-primary" }) => (
  <div className="glass rounded-xl p-4">
    <div className="flex items-center gap-2 text-muted-foreground mb-1">
      <Icon className={`w-4 h-4 ${color}`} />
      <span className="text-xs">{label}</span>
    </div>
    <p className="text-2xl font-bold font-display">{value}</p>
    {subValue && <p className="text-xs text-muted-foreground">{subValue}</p>}
  </div>
);

// Top products table
const TopProductsTable = ({ products }) => {
  if (!products?.length) {
    return <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border">
            <th className="text-left py-2 font-medium">Producto</th>
            <th className="text-right py-2 font-medium">Cantidad</th>
            <th className="text-right py-2 font-medium">Total</th>
          </tr>
        </thead>
        <tbody>
          {products.map((p, idx) => (
            <tr key={p.product_id || idx} className="border-b border-border/50">
              <td className="py-2">{p.product_name}</td>
              <td className="text-right py-2">{p.quantity}</td>
              <td className="text-right py-2 font-medium">${p.total?.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Sales by day chart (simple bar representation)
const SalesByDayChart = ({ data }) => {
  if (!data?.length) {
    return <p className="text-sm text-muted-foreground text-center py-4">Sin datos</p>;
  }

  const maxTotal = Math.max(...data.map(d => d.total || 0));

  return (
    <div className="space-y-2">
      {data.map((day, idx) => (
        <div key={day.date || idx} className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground w-20">
            {new Date(day.date + 'T12:00:00').toLocaleDateString('es-MX', { 
              weekday: 'short', 
              day: 'numeric' 
            })}
          </span>
          <div className="flex-1 h-6 bg-secondary rounded-full overflow-hidden">
            <div 
              className="h-full bg-primary rounded-full transition-all"
              style={{ width: `${maxTotal ? (day.total / maxTotal) * 100 : 0}%` }}
            />
          </div>
          <span className="text-sm font-medium w-24 text-right">${day.total?.toFixed(0)}</span>
        </div>
      ))}
    </div>
  );
};

export default function POSReports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0],
  });
  const [salesReport, setSalesReport] = useState(null);
  const [inventoryReport, setInventoryReport] = useState(null);

  useEffect(() => {
    fetchReports();
  }, []); // Initial fetch only on mount

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [salesRes, invRes] = await Promise.all([
        posAPI.getSalesReport(dateRange.start, dateRange.end),
        posAPI.getInventoryReport()
      ]);
      
      setSalesReport(salesRes.data);
      setInventoryReport(invRes.data);
    } catch (err) {
      console.error('Error fetching reports:', err);
      toast.error('Error cargando reportes');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleRefresh = () => {
    fetchReports();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  // Quick date range presets
  const setPreset = (days) => {
    const end = new Date();
    const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000);
    setDateRange({
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b border-border">
        <div className="flex items-center justify-between p-4 max-w-6xl mx-auto">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/pos')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-display font-bold text-lg">Reportes</h1>
              <p className="text-xs text-muted-foreground">Análisis de ventas e inventario</p>
            </div>
          </div>
          
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-4 space-y-6">
        {/* Date Range Selector */}
        <div className="glass rounded-xl p-4">
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <span className="font-medium">Período de ventas</span>
            <div className="flex gap-2 ml-auto">
              <Button variant="outline" size="sm" onClick={() => setPreset(7)}>7 días</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(30)}>30 días</Button>
              <Button variant="outline" size="sm" onClick={() => setPreset(90)}>90 días</Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Desde:</label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => handleDateChange('start', e.target.value)}
                className="h-9 px-3 rounded-md border border-input bg-background text-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm text-muted-foreground">Hasta:</label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => handleDateChange('end', e.target.value)}
                className="h-9 px-3 rounded-md border border-input bg-background text-sm"
              />
            </div>
            <Button size="sm" onClick={fetchReports}>
              Aplicar
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">
            Cargando reportes...
          </div>
        ) : (
          <>
            {/* Sales Summary */}
            <div>
              <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Resumen de Ventas
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                  icon={DollarSign}
                  label="Total Ventas"
                  value={formatCurrency(salesReport?.summary?.total_sales)}
                  subValue={`${salesReport?.summary?.num_transactions || 0} transacciones`}
                  color="text-green-500"
                />
                <StatCard
                  icon={Banknote}
                  label="Efectivo"
                  value={formatCurrency(salesReport?.summary?.total_cash)}
                  color="text-green-500"
                />
                <StatCard
                  icon={CreditCard}
                  label="Tarjeta"
                  value={formatCurrency(salesReport?.summary?.total_card)}
                  color="text-blue-500"
                />
                <StatCard
                  icon={ShoppingCart}
                  label="Otros"
                  value={formatCurrency(salesReport?.summary?.total_other)}
                  color="text-purple-500"
                />
              </div>
            </div>

            {/* Sales by Day */}
            <div className="glass rounded-xl p-4">
              <h3 className="font-medium mb-4">Ventas por Día</h3>
              <SalesByDayChart data={salesReport?.by_date} />
            </div>

            {/* Top Products */}
            <div className="glass rounded-xl p-4">
              <h3 className="font-medium mb-4">Productos Más Vendidos</h3>
              <TopProductsTable products={salesReport?.by_product} />
            </div>

            {/* Inventory Summary */}
            <div>
              <h2 className="font-display font-bold text-lg mb-3 flex items-center gap-2">
                <Package className="w-5 h-5" />
                Estado del Inventario
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard
                  icon={Package}
                  label="Productos Activos"
                  value={inventoryReport?.summary?.total_products || 0}
                />
                <StatCard
                  icon={DollarSign}
                  label="Valor Total"
                  value={formatCurrency(inventoryReport?.summary?.total_value)}
                  subValue="Precio venta"
                />
                <StatCard
                  icon={DollarSign}
                  label="Costo Total"
                  value={formatCurrency(inventoryReport?.summary?.total_cost)}
                  subValue="Precio compra"
                />
                <StatCard
                  icon={TrendingUp}
                  label="Ganancia Potencial"
                  value={formatCurrency(inventoryReport?.summary?.potential_profit)}
                  color="text-green-500"
                />
              </div>
            </div>

            {/* Low Stock Alert */}
            {inventoryReport?.low_stock?.length > 0 && (
              <div className="glass rounded-xl p-4 border-l-4 border-yellow-500">
                <h3 className="font-medium mb-3 flex items-center gap-2 text-yellow-500">
                  <AlertTriangle className="w-5 h-5" />
                  Productos con Stock Bajo ({inventoryReport.low_stock.length})
                </h3>
                <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
                  {inventoryReport.low_stock.map((product) => (
                    <div 
                      key={product.id} 
                      className="flex items-center justify-between p-2 bg-secondary/50 rounded-lg"
                    >
                      <span className="text-sm truncate flex-1">{product.name}</span>
                      <span className="text-sm font-bold text-yellow-500 ml-2">
                        {product.stock}/{product.min_stock}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Out of Stock */}
            {inventoryReport?.out_of_stock?.length > 0 && (
              <div className="glass rounded-xl p-4 border-l-4 border-red-500">
                <h3 className="font-medium mb-3 flex items-center gap-2 text-red-500">
                  <AlertTriangle className="w-5 h-5" />
                  Sin Stock ({inventoryReport.out_of_stock.length})
                </h3>
                <div className="flex flex-wrap gap-2">
                  {inventoryReport.out_of_stock.map((product) => (
                    <span 
                      key={product.id} 
                      className="px-3 py-1 bg-red-500/20 text-red-500 rounded-full text-sm"
                    >
                      {product.name}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
